import type { ReactElement } from 'react'
import type {
  ImageResponseNodeOptions,
  ImageResponseOptions,
  FigmaImageResponseProps,
} from './types'

import satoriMod, { init as initSatori } from 'satori/wasm'
import initYoga from 'yoga-wasm-web'
import * as resvg from '@resvg/resvg-wasm'

import { Readable } from 'stream'
import fs from 'fs'
import { fileURLToPath } from 'url'

import render from './og'
import { FigmaImageResponse } from './figma'

// @ts-ignore
const satori = satoriMod.default || satoriMod

const fontData = fs.readFileSync(
  fileURLToPath(new URL('./noto-sans-v27-latin-regular.ttf', import.meta.url))
)
const yoga_wasm = fs.readFileSync(
  fileURLToPath(new URL('./yoga.wasm', import.meta.url))
)
const resvg_wasm = fs.readFileSync(
  fileURLToPath(new URL('./resvg.wasm', import.meta.url))
)

const initializedResvg = resvg.initWasm(resvg_wasm)
const initializedYoga = initYoga(yoga_wasm).then((yoga) => initSatori(yoga))

export class ImageResponse extends Response {
  constructor(element: ReactElement, options: ImageResponseOptions = {}) {
    // This is a workaround for Next.js to detect if the API is used in
    // pages/api or app API routes. When used in pages/api, we can't return
    // a Response instance and need another interface instead.
    if (
      typeof Response === 'undefined' ||
      typeof ReadableStream === 'undefined'
    ) {
      throw new Error(
        'The `ImageResponse` API is not supported in this runtime, use the `unstable_createNodejsStream` API instead or switch to the Vercel Edge Runtime.'
      )
    }

    const result = new ReadableStream({
      async start(controller) {
        await initializedYoga
        await initializedResvg
        const fonts = [
          {
            name: 'sans serif',
            data: fontData,
            weight: 700,
            style: 'normal',
          },
        ]

        const result = await render(satori, resvg, options, fonts, element)

        controller.enqueue(result)
        controller.close()
      },
    })

    super(result, {
      headers: {
        'content-type': 'image/png',
        'cache-control':
          process.env.NODE_ENV === 'development'
            ? 'no-cache, no-store'
            : 'public, immutable, no-transform, max-age=31536000',
        ...options.headers,
      },
      status: options.status,
      statusText: options.statusText,
    })
  }
}

/**
 * Creates a pipeable stream of the rendered image in a lambda function.
 * All parameters are the same as `ImageResponse`.
 * @example
 * ```js
 * import { unstable_createNodejsStream } from '@vercel/og'
 *
 * export default async (req, res) => {
 *   const stream = await unstable_createNodejsStream(<div>Hello World</div>, { ... })
 *   res.setHeader('Content-Type', 'image/png')
 *   res.setHeader('Cache-Control', 'public, max-age=31536000, immutable')
 *   res.statusCode = 200
 *   res.statusMessage = 'OK'
 *   stream.pipe(res)
 * }
 * ```
 */
export async function unstable_createNodejsStream(
  element: ReactElement,
  options: Omit<
    ImageResponseNodeOptions,
    'status' | 'statusText' | 'headers'
  > = {}
) {
  await initializedYoga
  await initializedResvg
  const fonts = [
    {
      name: 'sans serif',
      data: fontData,
      weight: 700,
      style: 'normal',
    },
  ]

  const result = await render(satori, resvg, options, fonts, element)
  return Readable.from(Buffer.from(result))
}

export const experimental_FigmaImageResponse = async (
  props: FigmaImageResponseProps
) => {
  return FigmaImageResponse({ ...props, Response: ImageResponse })
}

export type NodeImageResponse = typeof ImageResponse
